import React, { createContext, useContext, useState, useEffect } from 'react';

interface FontSizeContextType {
  fontSize: number;
  increaseFontSize: () => void;
  decreaseFontSize: () => void;
  resetFontSize: () => void;
}

const FontSizeContext = createContext<FontSizeContextType | undefined>(undefined);

const DEFAULT_FONT_SIZE = 18;
const MIN_FONT_SIZE = 12;
const MAX_FONT_SIZE = 28;
const FONT_SIZE_STEP = 2;

export const FontSizeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [fontSize, setFontSize] = useState<number>(() => {
    const saved = localStorage.getItem('blue-links-font-size');
    if (saved) {
      const parsedSize = parseInt(saved, 10);
      if (!isNaN(parsedSize) && parsedSize >= MIN_FONT_SIZE && parsedSize <= MAX_FONT_SIZE) {
        return parsedSize;
      }
    }
    return DEFAULT_FONT_SIZE;
  });

  const increaseFontSize = () => {
    setFontSize(prev => {
      const newSize = Math.min(prev + FONT_SIZE_STEP, MAX_FONT_SIZE);
      localStorage.setItem('blue-links-font-size', newSize.toString());
      return newSize;
    });
  };

  const decreaseFontSize = () => {
    setFontSize(prev => {
      const newSize = Math.max(prev - FONT_SIZE_STEP, MIN_FONT_SIZE);
      localStorage.setItem('blue-links-font-size', newSize.toString());
      return newSize;
    });
  };

  const resetFontSize = () => {
    setFontSize(DEFAULT_FONT_SIZE);
    localStorage.setItem('blue-links-font-size', DEFAULT_FONT_SIZE.toString());
  };

  useEffect(() => {
    localStorage.setItem('blue-links-font-size', fontSize.toString());
  }, [fontSize]);

  return (
    <FontSizeContext.Provider value={{ 
      fontSize, 
      increaseFontSize, 
      decreaseFontSize, 
      resetFontSize 
    }}>
      {children}
    </FontSizeContext.Provider>
  );
};

export const useFontSize = () => {
  const context = useContext(FontSizeContext);
  if (!context) {
    throw new Error('useFontSize must be used within FontSizeProvider');
  }
  return context;
};